import { z } from 'zod';

import {
  type CashCountedDenominationSnapshotDto,
  type CashDenominationSnapshotConfidence,
  type CashDenominationSnapshotKind,
  type CreateCashCountedDenominationSnapshotRequest,
  type CreateCashCountedDenominationSnapshotResponse,
  type GetCashCountedDenominationSnapshotRequest,
  type GetCashCountedDenominationSnapshotResponse,
  type ListCashCountedDenominationSnapshotsRequest,
  type ListCashCountedDenominationSnapshotsResponse,
  cashDenominationSnapshotConfidenceValues,
  cashDenominationSnapshotKindValues,
  supportedCashDenominationMinorValues,
} from './cash-denomination-snapshots.dto.js';
import { type CashDenominationDto } from './cash-sessions.dto.js';

const timestampSchema = z.iso.datetime();
const supportedDenominations = new Set<number>(supportedCashDenominationMinorValues);

export const CashDenominationSnapshotKindSchema: z.ZodType<CashDenominationSnapshotKind> = z.enum(
  cashDenominationSnapshotKindValues,
);

export const CashDenominationSnapshotConfidenceSchema: z.ZodType<CashDenominationSnapshotConfidence> =
  z.enum(cashDenominationSnapshotConfidenceValues);

export const CashSupportedDenominationSchema: z.ZodType<CashDenominationDto> = z.object({
  denom: z
    .number()
    .int()
    .positive()
    .refine((value) => supportedDenominations.has(value), 'Unsupported denomination'),
  count: z.number().int().nonnegative(),
});

const denominationRowsSchema = z.array(CashSupportedDenominationSchema);

export const CashCountedDenominationSnapshotDtoSchema: z.ZodType<CashCountedDenominationSnapshotDto> =
  z
    .object({
      id: z.uuid(),
      companyId: z.uuid(),
      storeId: z.uuid().nullable(),
      locationId: z.uuid().nullable(),
      stationId: z.uuid().nullable(),
      cashLocationId: z.uuid(),
      snapshotKind: CashDenominationSnapshotKindSchema,
      confidence: CashDenominationSnapshotConfidenceSchema,
      denominations: denominationRowsSchema,
      countedTotalMinor: z.number().int().nonnegative(),
      countedByUserId: z.uuid(),
      countedAt: timestampSchema,
      cashSessionId: z.uuid().nullable(),
      note: z.string().trim().min(1).max(500).nullable(),
      createdAt: timestampSchema,
      updatedAt: timestampSchema,
    })
    .superRefine((value, context) => {
      addEmptyDenominationsIssue(value.denominations, value.countedTotalMinor, context);
      addDuplicateDenominationIssues(value.denominations, context);
      addTotalMismatchIssue(value.denominations, value.countedTotalMinor, context);
      addSnapshotScopeIssues(value, context);
    });

export const CreateCashCountedDenominationSnapshotRequestSchema: z.ZodType<CreateCashCountedDenominationSnapshotRequest> =
  z
    .object({
      companyId: z.uuid(),
      operationId: z.uuid(),
      occurredAt: timestampSchema,
      storeId: z.uuid().nullable().optional(),
      locationId: z.uuid().nullable().optional(),
      stationId: z.uuid().nullable().optional(),
      cashLocationId: z.uuid(),
      snapshotKind: CashDenominationSnapshotKindSchema,
      confidence: z.literal('counted'),
      denominations: denominationRowsSchema,
      countedTotalMinor: z.number().int().nonnegative(),
      countedAt: timestampSchema,
      cashSessionId: z.uuid().nullable().optional(),
      note: z.string().trim().min(1).max(500).nullable().optional(),
    })
    .superRefine((value, context) => {
      addEmptyDenominationsIssue(value.denominations, value.countedTotalMinor, context);
      addDuplicateDenominationIssues(value.denominations, context);
      addTotalMismatchIssue(value.denominations, value.countedTotalMinor, context);
      addSnapshotScopeIssues(value, context);
    });

export const CreateCashCountedDenominationSnapshotResponseSchema: z.ZodType<CreateCashCountedDenominationSnapshotResponse> =
  z.object({
    snapshot: CashCountedDenominationSnapshotDtoSchema,
  });

export const ListCashCountedDenominationSnapshotsRequestSchema: z.ZodType<ListCashCountedDenominationSnapshotsRequest> =
  z.object({
    companyId: z.uuid(),
    storeId: z.uuid().optional(),
    locationId: z.uuid().optional(),
    stationId: z.uuid().optional(),
    cashLocationId: z.uuid().optional(),
    cashSessionId: z.uuid().optional(),
    snapshotKind: CashDenominationSnapshotKindSchema.optional(),
    from: timestampSchema.optional(),
    to: timestampSchema.optional(),
  });

export const ListCashCountedDenominationSnapshotsResponseSchema: z.ZodType<ListCashCountedDenominationSnapshotsResponse> =
  z.object({
    items: z.array(CashCountedDenominationSnapshotDtoSchema),
  });

export const GetCashCountedDenominationSnapshotRequestSchema: z.ZodType<GetCashCountedDenominationSnapshotRequest> =
  z.object({
    companyId: z.uuid(),
    snapshotId: z.uuid(),
  });

export const GetCashCountedDenominationSnapshotResponseSchema: z.ZodType<GetCashCountedDenominationSnapshotResponse> =
  z.object({
    snapshot: CashCountedDenominationSnapshotDtoSchema,
  });

function addTotalMismatchIssue(
  denominations: CashDenominationDto[],
  countedTotalMinor: number,
  context: z.RefinementCtx,
): void {
  const total = denominations.reduce((sum, entry) => sum + entry.denom * entry.count, 0);
  if (total !== countedTotalMinor) {
    context.addIssue({
      code: 'custom',
      message: 'Counted denomination total does not match countedTotalMinor.',
      path: ['countedTotalMinor'],
    });
  }
}

function addEmptyDenominationsIssue(
  denominations: CashDenominationDto[],
  countedTotalMinor: number,
  context: z.RefinementCtx,
): void {
  if (denominations.length > 0 || countedTotalMinor === 0) {
    return;
  }

  context.addIssue({
    code: 'custom',
    message: 'Empty denomination rows are only valid for an explicit zero count.',
    path: ['denominations'],
  });
}

function addDuplicateDenominationIssues(
  denominations: CashDenominationDto[],
  context: z.RefinementCtx,
): void {
  const seen = new Set<number>();
  for (const [index, entry] of denominations.entries()) {
    if (seen.has(entry.denom)) {
      context.addIssue({
        code: 'custom',
        message: 'Duplicate denomination rows are not supported.',
        path: ['denominations', index, 'denom'],
      });
      continue;
    }
    seen.add(entry.denom);
  }
}

function addSnapshotScopeIssues(
  value: {
    snapshotKind: CashDenominationSnapshotKind;
    storeId?: string | null;
    locationId?: string | null;
    stationId?: string | null;
    cashSessionId?: string | null;
  },
  context: z.RefinementCtx,
): void {
  switch (value.snapshotKind) {
    case 'drawerManualCount':
      requireScopeValue('storeId', value.storeId, context);
      requireScopeValue('locationId', value.locationId, context);
      requireScopeValue('stationId', value.stationId, context);
      return;
    case 'vaultManualCount':
      requireScopeValue('storeId', value.storeId, context);
      requireScopeValue('locationId', value.locationId, context);
      rejectScopeValue('stationId', value.stationId, context);
      rejectScopeValue('cashSessionId', value.cashSessionId, context);
      return;
    case 'treasuryManualCount':
      rejectScopeValue('storeId', value.storeId, context);
      rejectScopeValue('locationId', value.locationId, context);
      rejectScopeValue('stationId', value.stationId, context);
      rejectScopeValue('cashSessionId', value.cashSessionId, context);
      return;
  }
}

function requireScopeValue(
  field: 'storeId' | 'locationId' | 'stationId',
  value: string | null | undefined,
  context: z.RefinementCtx,
): void {
  if (value) {
    return;
  }

  context.addIssue({
    code: 'custom',
    message: `${field} is required for this denomination snapshot kind.`,
    path: [field],
  });
}

function rejectScopeValue(
  field: 'storeId' | 'locationId' | 'stationId' | 'cashSessionId',
  value: string | null | undefined,
  context: z.RefinementCtx,
): void {
  if (value === null || value === undefined) {
    return;
  }

  context.addIssue({
    code: 'custom',
    message: `${field} must be omitted or null for this denomination snapshot kind.`,
    path: [field],
  });
}
